import type { ReactNode } from 'react';
import { FlaskConical, ShieldAlert } from 'lucide-react';

// The staging surface that hosts the apparatus (passed as children) plus a
// step-progress header, an active-step strip, an optional settling/processing
// bar and an optional safety banner. Restyled to ACE's --ws-* design tokens.
//
// Backward compatible: only NEW optional props were added (currentStepLabel,
// phase, statusTone, safetyNote, footer). Existing callers pass the original
// three required props and get the original behaviour.

type StatusTone = 'idle' | 'active' | 'success' | 'warning' | 'danger';

export interface LabBenchProps {
  experimentName: string;
  completedCount: number;
  totalSteps: number;
  children?: ReactNode;
  /** 0-1, shows a processing bar when > 0. */
  settlingProgress?: number;
  /** Short label for the step the learner is on (rendered under the header). */
  currentStepLabel?: string;
  /** Free phase text shown right-aligned in the active strip (e.g. "Titrating"). */
  phase?: string;
  /** Drives the header accent/edge color. */
  statusTone?: StatusTone;
  /** A standing safety reminder shown as a slim banner above the stage. */
  safetyNote?: string;
  /** Optional content rendered in a footer band under the stage. */
  footer?: ReactNode;
}

const TONE_BAR: Record<StatusTone, string> = {
  idle: 'bg-ws-accent',
  active: 'bg-ws-accent',
  success: 'bg-ws-success',
  warning: 'bg-ws-warning',
  danger: 'bg-ws-danger',
};

const TONE_EDGE: Record<StatusTone, string> = {
  idle: 'before:bg-ws-border',
  active: 'before:bg-ws-accent',
  success: 'before:bg-ws-success',
  warning: 'before:bg-ws-warning',
  danger: 'before:bg-ws-danger',
};

export default function LabBench({
  experimentName,
  completedCount,
  totalSteps,
  children,
  settlingProgress,
  currentStepLabel,
  phase,
  statusTone = 'idle',
  safetyNote,
  footer,
}: LabBenchProps) {
  const pct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  return (
    <div
      className={`relative flex flex-1 flex-col overflow-visible rounded-md border border-ws-border bg-ws-surface shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-md before:content-[''] ${TONE_EDGE[statusTone]}`}
    >
      {/* Header */}
      <div className="flex flex-shrink-0 flex-col gap-1.5 rounded-t-md bg-ws-accent px-4 py-2.5 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <FlaskConical size={16} className="flex-shrink-0" />
            <h3 className="m-0 truncate text-sm font-semibold">Lab Bench — {experimentName}</h3>
          </div>
          <span className="flex-shrink-0 text-xs font-medium tabular-nums opacity-90">
            {completedCount}/{totalSteps} · {pct}%
          </span>
        </div>
        {/* Progress track */}
        <div className="h-1 w-full overflow-hidden rounded-full bg-white/25">
          <div
            className="h-full rounded-full bg-white transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Active-step strip */}
      {(currentStepLabel || phase) && (
        <div className="flex items-center justify-between gap-3 border-b border-ws-border bg-ws-surface-2 px-4 py-1.5">
          <div className="flex min-w-0 items-center gap-2 text-xs text-ws-ink-2">
            <span className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${TONE_BAR[statusTone]} ${statusTone === 'active' ? 'animate-pulse' : ''}`} />
            <span className="truncate">{currentStepLabel}</span>
          </div>
          {phase && (
            <span className="flex-shrink-0 rounded-md bg-ws-accent-quiet px-2 py-0.5 text-[11px] font-medium text-ws-accent-ink">
              {phase}
            </span>
          )}
        </div>
      )}

      {/* Safety banner */}
      {safetyNote && (
        <div className="flex items-center gap-2 border-b border-ws-warning/40 bg-ws-warning/10 px-4 py-1.5 text-[11px] text-ws-ink-2">
          <ShieldAlert size={13} className="flex-shrink-0 text-ws-warning" />
          <span className="truncate">{safetyNote}</span>
        </div>
      )}

      {/* Stage */}
      <div className="relative flex flex-1 flex-col items-center justify-center bg-ws-surface-2 p-4">
        {settlingProgress !== undefined && settlingProgress > 0 && (
          <div className="absolute left-1/2 top-5 z-10 flex h-6 w-52 -translate-x-1/2 items-center justify-center overflow-hidden rounded-full bg-ws-border">
            <div
              className="absolute left-0 top-0 h-full bg-ws-accent transition-[width] duration-300"
              style={{ width: `${settlingProgress * 100}%` }}
            />
            <span className="relative text-xs font-semibold text-white drop-shadow">
              {Math.round(settlingProgress * 100)}%
            </span>
          </div>
        )}

        <div className="flex w-full flex-col items-center gap-4">{children}</div>
      </div>

      {/* Footer band */}
      {footer && (
        <div className="flex-shrink-0 rounded-b-md border-t border-ws-border bg-ws-surface px-4 py-2">
          {footer}
        </div>
      )}
    </div>
  );
}
